import API from '@api';
import APIold from '@api/graphqlOld';
import APIStage from '@api/graphqlStageOld';
import { queryClient } from '@root/src/providers/query';
import {
  activeStoreCitiesQueryOptions,
  shopsQueryOptions
} from '@queries/shops';
import {
  COURIER_DELIVERY_ID,
  EXPRESS_DELIVERY_INTERVAL_ID
} from '@constants/delivery';
import {
  MOSCOW_DEFAULT_STORE_ID,
  MOSCOW_REGION_ID,
  OTHER_REGIONS_DEFAULT_STORE_IDS
} from '@constants/regions';
import { IEdetector } from '@utils/browserUTLs';
import { isOtherRegion } from '@utils/regions';
import { notifications, showGiftNotification } from '@utils/notifications';
import { increaseCounter } from '@utils/yandexMetrica';
import { syncFlocktoryCart } from '@utils/flocktory/cart';
import type { PrepareGiftToSelectReturnType } from '@utils/orderUtils/prepareGiftToSelect';
import prepareGiftToSelect from '@utils/orderUtils/prepareGiftToSelect';
import buildCartGifts from '@utils/orderUtils/buildCartGifts';
import debounce from 'lodash/debounce';
import type { DebouncedFunc } from 'lodash';
import {
  action,
  computed,
  configure,
  makeObservable,
  observable,
  when
} from 'mobx';
import { v1 } from 'uuid';
import ConfigStore from './ConfigStore';
import XStore from './XStore';
import { getProductCanBeBought } from '@utils/getProductCanBeBought';
import fetchWithTimeout from '@utils/fetchWithTimeout';
import type {
  CartT,
  ServicesAvailableT,
  GiftArrItem,
  ConfigValues,
  SpecialPriceItem,
  NearestDeliveryInterval,
  CartProductDebounceObj,
  NextLevelGift,
  InitCartStoreParams,
  GetBarMenuCartParams,
  GetCartParams,
  BulkProductItem,
  UpdateCartMainFunc,
  DeliveryInterval,
  PaymentOption,
  RegionData,
  Range,
  Coordinates
} from './CartStore.type';
import type {
  GetRecommendationsToCartQuery,
  SaveCartTemplateItemMutation
} from '@schema/cart';
import {
  ImgeFilterE,
  type AddMultipleProductsToCartMutationVariables,
  type AddProductToCartMutation,
  type AddProductToCartMutationVariables,
  type AddProductToPostponedListMutation,
  type ApplyPromoCodeMutation,
  type CartPostcardInput,
  type CartPostcardsModel,
  type CheckCartChangesQuery,
  type ConfirmProfileBySmsMutation,
  type ConfirmProfileBySmsMutationVariables,
  type CreateOrderFromCartMutation,
  type CreateOrderFromCartMutationVariables,
  type GetAmountStoresQuery,
  type GetDeliveryIntervalsQuery,
  type GetDeliveryIntervalsQueryVariables,
  type GetPaymentOptionsQuery,
  type GetPaymentOptionsQueryVariables,
  type GetReplacedProductCartQuery,
  type MergeCartsMutation,
  type MergeCartsMutationVariables,
  type NearestDeliveryQuery,
  type PayOrderWithBonusesMutation,
  type PickupPointByIdQuery,
  type PickupPointsQuery,
  type RemoveCartItemMutation,
  type RemoveCartItemMutationVariables,
  type RemoveProductFromPostponedListMutation,
  type ShareCartQuery,
  type ShopFragmentMainEndpointFragment,
  type StoreCityItemFragmentFragment,
  type TodayLatestIntervalQuery,
  type UpdateCartInput,
  type UpdateCartLoyaltyProgramMutationVariables,
  type UpdateCartMutation,
  type UpdateCartMutationVariables,
  type UpdateOrCreateMenuCartMutation
} from '@schema/main';
import type { Flatten } from '../types';
import type { ApiT } from '../api/graphql';
import type { ConfigData } from './ConfigStore.type';

const debounceTime = 200;
const barMenuOnlinePaymentId = 22;

configure({
  enforceActions: 'observed',
  useProxies: IEdetector() ? 'never' : 'always'
});

// ЭТАП 1 · Шаг 3 — SSR-safe: localStorage читается на module-eval, на сервере его нет.
const cartTokenLocalStorage =
  typeof localStorage !== 'undefined' ? localStorage.getItem('cartToken') : null;

export let cartToken: string = cartTokenLocalStorage
  ? (JSON.parse(cartTokenLocalStorage) as string)
  : v1();

try {
  if (typeof localStorage !== 'undefined' && !localStorage.getItem('cartToken')) {
    localStorage.setItem('cartToken', JSON.stringify(cartToken));
  }
} catch (e) {
  console.log(e);
}

const changeCartToken = (token: string): void => {
  cartToken = token;
};

const persistCartToken = (token: string): void => {
  changeCartToken(token);
  try {
    localStorage.setItem('cartToken', JSON.stringify(token));
  } catch (e) {
    console.log(e);
  }
};

const getSavedCartToken = (): string => {
  try {
    const saved = localStorage.getItem('cartToken');
    return saved ? (JSON.parse(saved) as string) : '';
  } catch (e) {
    console.log(e);
    return '';
  }
};

const BAR_MENU_CART_TOKEN_KEY = 'barMenuCartToken';

const getSavedBarMenuCartToken = (): string => {
  try {
    const saved = localStorage.getItem(BAR_MENU_CART_TOKEN_KEY);
    return saved ? (JSON.parse(saved) as string) : '';
  } catch (e) {
    console.log(e);
    return '';
  }
};

const saveBarMenuCartToken = (token: string): void => {
  try {
    localStorage.setItem(BAR_MENU_CART_TOKEN_KEY, JSON.stringify(token));
  } catch (e) {
    console.log(e);
  }
};

// ЭТАП 1 · Шаг 3 — SSR-safe: на сервере localStorage нет.
if (typeof localStorage !== 'undefined') {
  try {
    Object.keys(localStorage)
      .filter(key => key.startsWith(`${BAR_MENU_CART_TOKEN_KEY}:`))
      .forEach(key => localStorage.removeItem(key));
  } catch (e) {
    console.log(e);
  }
}

const getGiftArr = (
  products?: Array<{
    id?: number | null | undefined;
    title?: string | null | undefined;
  }> | null,
  services?: Array<{
    id?: number | null | undefined;
    title?: string | null | undefined;
  }> | null
): GiftArrItem[] => {
  const gifts: GiftArrItem[] = [];
  if (products && products.length > 0) {
    products.map(a => gifts.push({ ...a, type: 'product' }));
  }

  if (services && services.length > 0) {
    services.map(a => gifts.push({ ...a, type: 'service' }));
  }
  return gifts;
};
let updateCartTimer: ReturnType<typeof setTimeout> | undefined;

export const DEFAULT_CART: CartT = {
  items: [],
  services: [],
  gifts: [],
  testDrives: [],
  possibleBonus: {
    loyaltyProgram: {} as any
  } as any,
  people: {
    bill: {
      billAmount: 0
    }
  },
  promoCodes: [],
  payment: {} as any,
  peopleAddress: {
    id: null,
    region_city_id: null,
    isValidForExpress: null,
    dadata: {} as any,
    storeItem: null
  },
  total: 0,
  subtotal: 0,
  cartLoyaltyProgram: [],
  needReceipt: true,
  defaultCart: true,
  delivery_id: 1
  // store_id: 1
};

const defaultConfig: ConfigValues = {
  morningStart: null,
  morningEnd: null,
  eveningStart: null,
  eveningEnd: null
};

const defaultSpecialPrice: SpecialPriceItem[] = [
  {
    value: null,
    productId: null,
    text: '',
    title: '',
    code: ''
  }
];

const initialState = {
  cart: DEFAULT_CART,

  availableServices: [],

  specialPrice: defaultSpecialPrice,

  productsAmount: {},

  productsAmountMultiple: {},

  serviceAmount: {},

  rubrics: {},

  deliveryIntervals: [],

  expressIntervals: [],

  paymentOptions: [],

  configValues: defaultConfig,

  amountStores: [],
  // maxAvailableQuantityInOneOrder:[]

  shouldCheckExpress: false,

  isLoadCart: false,

  nearestDeliveryInterval: {
    date: '',
    interval: ''
  },

  cartCounter: 0,

  cartProductDebounceFuncs: {
    // example [id]:  {
    //   func:debounce((...args) => this.updateCartMainFunc(...args), 3000)
    //   isRemove: false,
    //   callback
    // }
  },

  cartServicesDebounceFuncs: {
    // example [id]: debounce((...args) => this.updateCartMainFunc(...args), 3000)
  },

  deliveryIntervalsCounter: 0,

  hasGift: false,

  hasGiftInitial: true,

  dayOfWeek: null,

  isMerged: false,

  isOpenPostCard: false,

  flowersPostcards: [],

  cartTokenEdit: '',

  flowersAmount: 0,

  notificationQueue: new Map(),

  billPayed: 0,

  shops: [],
  isShopsLoading: true,

  isSelectExpressDel: false,

  promocodeInputValue: '',

  regions: [],

  cartType: 'default' // default, bar-menu
};

class Store {
  cart: CartT;

  cartType: 'default' | 'bar-menu';

  productsAmount: Record<number, number>;

  productsAmountMultiple: Record<number, number>;

  billPayed: number;

  specialPrice: SpecialPriceItem[];

  serviceAmount: Record<number, number>;

  rubrics: Record<string, string>;

  availableServices: ServicesAvailableT;

  deliveryIntervals: DeliveryInterval[];

  expressIntervals: NonNullable<
    GetDeliveryIntervalsQuery['getDeliveryIntervals']['express']
  >;

  paymentOptions: PaymentOption[];

  configValues: ConfigValues;

  amountStores: GetAmountStoresQuery['getAmountStores'];

  shouldCheckExpress: boolean;

  isLoadCart: boolean;

  nearestDeliveryInterval: NearestDeliveryInterval;

  cartCounter: number;

  cartProductDebounceFuncs: Record<number, CartProductDebounceObj>;

  cartServicesDebounceFuncs: Record<
    number,
    DebouncedFunc<(...args: any[]) => Promise<any>>
  >;

  deliveryIntervalsCounter: number;

  hasGift: boolean;

  hasGiftInitial: boolean;

  dayOfWeek: number | null;

  isMerged: boolean;

  isOpenPostCard: number | null;

  flowersPostcards: CartPostcardsModel[];

  cartTokenEdit: string;

  flowersAmount: number;

  notificationQueue: Map<number, string>;

  shops: ShopFragmentMainEndpointFragment[];

  isShopsLoading: boolean;

  isSelectExpressDel: boolean;

  promocodeInputValue: string;

  regions: StoreCityItemFragmentFragment[];

  constructor() {
    Object.assign(this, initialState);
    makeObservable(this, {
      cart: observable,
      cartType: observable,
      productsAmount: observable,
      productsAmountMultiple: observable,
      billPayed: observable,
      specialPrice: observable,
      serviceAmount: observable,
      rubrics: observable,
      availableServices: observable,
      deliveryIntervals: observable,
      expressIntervals: observable,
      paymentOptions: observable,
      nearestDeliveryInterval: observable,
      configValues: observable,
      // maxAvailableQuantityInOneOrder: observable,
      dayOfWeek: observable,
      isMerged: observable,
      isLoadCart: observable,
      isOpenPostCard: observable,
      flowersPostcards: observable,
      cartTokenEdit: observable,
      flowersAmount: observable,
      promocodeInputValue: observable,
      isSelectExpressDel: observable,
      amountStores: observable,
      shouldCheckExpress: observable,
      shops: observable,
      isShopsLoading: observable,
      regions: observable,

      isLoadStories: computed,
      giftToSelect: computed,
      acceptedTestDrives: computed,
      cartGifts: computed,
      isUsedBonuses: computed,
      hasBonusRestrictedItems: computed,
      activePromocode: computed,
      canPromoBeCancelled: computed,
      orderBonusesAmount: computed,
      giftsList: computed,
      nextLevelGift: computed,
      loyaltyPercent: computed,
      isCartEmpty: computed,
      morningStart: computed,
      morningEnd: computed,
      eveningStart: computed,
      eveningEnd: computed,
      cartItemsCount: computed,
      regionData: computed,
      isCartAddressFromOtherRegion: computed,

      setShops: action,
      setIsShopsLoading: action,
      getShops: action,
      setIsLoadCart: action,
      setCartType: action,
      updatePackets: action,
      getCart: action,
      setCart: action,
      setServices: action,
      getUpdatedCart: action,
      removeCartItem: action,
      updateCart: action,
      updateCartMainFunc: action,
      updateCartMultiple: action,
      changeProductsAmount: action,
      bulkChangeProductsAmount: action,
      addProductToPostponed: action,
      removeProductFromPostponed: action,
      clearCart: action,
      setIsOpenPostCardModal: action,
      removeService: action,
      addServiceToCart: action,
      changeServicesAmount: action,
      // getNotAvailableGifts: action,
      payWithBonuses: action,
      applyPromoCode: action,
      removeCartPromoCode: action,

      getDeliveryIntervals: action,
      setIntervals: action,
      setExpressIntervals: action,

      getPaymentOptions: action,
      setPaymentOptions: action,

      updateCartStage: action,
      selectPickupStore: action,

      getNearestDeliveryInterval: action,
      setNearestDeliveryInterval: action,

      createOrderFromCart: action,
      // getMaxAvailableQuantityInOneOrder: action,
      // setMaxAvailableQuantityInOneOrder: action,

      repeatOrder: action,

      addItemsToCartFromTemplate: action,

      mergeCarts: action,

      getRecommendationsToCart: action,

      updateCartLoyaltyProgram: action,

      oneClickOrderCallback: action,

      withCartToken: action,
      withCartTokenSpread: action,

      // configRequests: action,
      setConfig: action,
      updateConfig: action,
      confirmProfileBySMS: action,

      setDayOfWeek: action,
      updateCartStageWithDay: action,

      // applicationForParticipationInTestDrive: action,
      cancelTestDriveApplication: action,

      changeMergeState: action,

      shareCart: action,
      rebindCartPid: action,

      onAddPostCard: action,

      setBillPayed: action,
      setPromocodeInputValue: action,

      getReplacedProductCart: action,
      setSelectExpressDel: action,

      setAmountStores: action,
      setShouldCheckExpress: action,
      modernTimer: action,
      resetStore: action
    });
  }

  get regionData(): RegionData | null {
    const regionData = this.cart.storeCityItem;

    if (!regionData) {
      return null;
    }

    const cityStoreItem = {
      ...regionData,
      fields: {
        ...(regionData.fields || {}),
        geo_center:
          regionData?.fields?.geo_center?.split(',')?.map(Number) || null,
        geo_suggestions: regionData?.fields?.geo_suggestions
          ?.slice(1, -1)
          ?.split(',')
          ?.map(Number)
      }
    };

    return cityStoreItem;
  }

  get isCartAddressFromOtherRegion(): boolean {
    const addressRegionId = this.cart?.peopleAddress?.region_city_id;
    const regionId = this.regionData?.id;

    return (
      addressRegionId !== null &&
      addressRegionId !== undefined &&
      regionId !== null &&
      regionId !== undefined &&
      Number(addressRegionId) !== Number(regionId)
    );
  }
  // getIsCurrentAddressBar () {
  //   return !!this.cart.peopleAddress;
  // }

  get openedShops(): ShopFragmentMainEndpointFragment[] {
    return this.shops?.filter(item => !item.opens_soon) || [];
  }

  get isCurrentAddressValidForExpress(): boolean {
    const isAddressValidForExpress =
      !!this.cart?.dadata?.isValidForExpress ||
      !!this.cart?.peopleAddress?.isValidForExpress;

    if (!isAddressValidForExpress) {
      return false;
    }

    if (Number(this.cart?.store_id) === MOSCOW_DEFAULT_STORE_ID) {
      return true;
    }

    return (
      this.cart?.delivery_id === COURIER_DELIVERY_ID &&
      this.cart?.deliveryIntervalId === EXPRESS_DELIVERY_INTERVAL_ID
    );
  }

  get cartItemsCount(): number {
    return Number(this.cart?.items?.length) > 0
      ? this?.cart?.items?.reduce(
          (sum, current) => sum + Number(current.amount),
          0
        ) || 0
      : 0;
  }

  get isCartEmpty(): boolean {
    return (
      Number(this.cart?.items?.length) === 0 &&
      this.cart?.services?.length === 0
    );
  }

  get morningStart(): string | null | undefined {
    return this.configValues.morningStart;
  }

  get morningEnd(): string | null | undefined {
    return this.configValues.morningEnd;
  }

  get eveningStart(): string | null | undefined {
    return this.configValues.eveningStart;
  }

  get eveningEnd(): string | null | undefined {
    return this.configValues.eveningEnd;
  }

  get loyaltyPercent(): number {
    let percent = 0;
    if (this.cart.possibleBonus?.loyaltyProgram) {
      if (
        (Number(this.cart.subtotal) * 100) /
          Number(this.cart?.possibleBonus?.loyaltyProgram?.minOrderPrice || 1) >
        100
      ) {
        percent = 100;
      } else {
        percent =
          (Number(this.cart.subtotal) * 100) /
          Number(this.cart.possibleBonus.loyaltyProgram.minOrderPrice || 1);
      }
    }
    return percent;
  }

  get nextLevelGift(): NextLevelGift | null {
    const nextLoyaltyLevels = this.cart?.possibleBonus?.nextLoyaltyLevels;
    if (nextLoyaltyLevels?.[0]) {
      const gift: NextLevelGift = {};
      if (nextLoyaltyLevels[0].freeDelivery) {
        gift.type = 'delivery';
        gift.title = nextLoyaltyLevels[0].comment;
        gift.minOrderPrice = nextLoyaltyLevels[0].minOrderPrice;
        gift.gifts = getGiftArr(
          nextLoyaltyLevels[0]?.productsAsGifts,
          nextLoyaltyLevels[0]?.servicesAsGifts
        );
      }
      return gift;
    }
    return null;
  }

  get giftsList(): GiftArrItem[] {
    const gifts = getGiftArr(
      this.cart.possibleBonus?.loyaltyProgram?.productsAsGifts || [],
      this.cart.possibleBonus?.loyaltyProgram?.servicesAsGifts || []
    );
    return gifts;
  }

  get orderBonusesAmount(): number {
    const amount =
      (Number(
        this.cart?.cartLoyaltyProgram?.[
          this.cart?.cartLoyaltyProgram?.length - 1
        ]?.loyaltyProgram?.bonusPercentage
      ) /
        100) *
      Number(this.cart?.total);

    return this.cart?.possibleBonus?.maxValue === 0 &&
      this.cart?.possibleBonus?.loyaltyProgram == null &&
      this.cart?.cartLoyaltyProgram?.length !== 0
      ? Math.round(amount)
      : Number(this.cart?.possibleBonus?.maxValue);
  }

  get isLoadStories(): boolean {
    return !!this.amountStores?.length;
  }

  get isUsedBonuses(): boolean {
    return !!this.billPayed;
  }

  get hasBonusRestrictedItems(): boolean {
    return !!this.cart?.items?.some(
      item => item?.catalogItem?.bonus_payment_enabled === 0
    );
  }

  get activePromocode(): Flatten<CartT['promoCodes']> | null {
    return (
      this.cart?.promoCodes?.filter(promo => promo?.promoCode?.code)?.[0] ||
      null
    );
  }

  get canPromoBeCancelled(): boolean {
    return (
      cartStore?.cart?.promoCodes?.some(
        item => item?.promoCode?.canBeCancelled === 1
      ) || false
    );
  }

  getShops = async (): Promise<void> => {
    // ensureQueryData: в пределах staleTime отдаёт из кэша без запроса,
    // параллельные вызовы дедуплицируются
    await queryClient.ensureQueryData(shopsQueryOptions()).then(res => {
      this.setShops(res.getShops || []);
      this.setIsShopsLoading();
    });
  };

  setShops = (shops: ShopFragmentMainEndpointFragment[] = []): void => {
    this.shops = shops;
  };

  setIsShopsLoading = (): void => {
    this.isShopsLoading = false;
  };

  setCartType = (cartType: 'default' | 'bar-menu'): void => {
    this.cartType = cartType;
  };

  setPromocodeInputValue = (payload: string): void => {
    this.promocodeInputValue = payload;
  };

  setBillPayed = (amount: number): void => {
    this.billPayed = amount;
  };

  get giftToSelect(): PrepareGiftToSelectReturnType {
    // {
    // activeGift,
    // uniqueProducts,
    // uniqueServices
    // }
    const state = prepareGiftToSelect({
      cartLoyaltyProgram: this.cart.cartLoyaltyProgram
    });
    return state;
  }

  get acceptedTestDrives(): Array<Record<string, any>> {
    const cart = this.cart;
    return [...(cart?.gifts || [])]
      .filter(item => item?.id === cart?.cartTestDrive?.product?.id)
      .map(item => ({
        testDriveId: cart?.cartTestDrive?.testDrive?.id,
        isTestDrive: true,
        ...item
      }));
  }

  get cartGifts(): Array<Record<string, any>> {
    return buildCartGifts({
      cart: this.cart,
      giftToSelect: this.giftToSelect
    });
  }

  getPickupPointById = async (
    storeId: number | string
  ): Promise<PickupPointByIdQuery> =>
    await APIStage.pickupPointById({
      cartToken,
      storeId: +storeId,
      imageFilter: ImgeFilterE.W480
    }).then(res => {
      this.setIntervals(res.pickupPointById?.deliveryIntervals || []);

      return res;
    });

  getPickupPoints = async (): Promise<PickupPointsQuery> =>
    await APIStage.pickupPoints({
      cartToken,
      imageFilter: ImgeFilterE.W480
    }).then(res => ({
      pickupPoints: res.pickupPoints
        .filter(item => !isOtherRegion(item.city_id))
        .map(item => ({
          ...item,
          fastDelivery: item.has_amount === 1
        }))
    }));

  getNearestDeliveryTypes = async (): Promise<NearestDeliveryQuery> =>
    await APIStage.nearestDelivery({ cartToken });

  modernTimer = (): void => {
    if (updateCartTimer) {
      clearTimeout(updateCartTimer);
    }

    updateCartTimer = setTimeout(() => {
      this.getCart({});
      this.modernTimer();
    }, 60000);
  };

  getDefaultCart = async (token: string = ''): Promise<void> => {
    let cart = await this.getCart({
      newToken: token || getSavedCartToken() || cartToken
    });
    if (cart?.is_menu) {
      cart = await this.getCart({ newToken: v1() });
    }
    persistCartToken(cart?.cookieId || cartToken);
    await this.ensureMoscowRegion(cart);
    syncFlocktoryCart(this.cart);
  };

  ensureMoscowRegion = async (cart: CartT): Promise<void> => {
    const isOtherRegionCity = isOtherRegion(cart?.storeCityItem?.id);
    const isOtherRegionStore = OTHER_REGIONS_DEFAULT_STORE_IDS.includes(
      Number(cart?.store_id)
    );

    if (!isOtherRegionCity && !isOtherRegionStore) {
      return;
    }

    try {
      await fetchWithTimeout(
        async () =>
          await this.updateCartStage({ storeId: MOSCOW_DEFAULT_STORE_ID }),
        10000,
        undefined,
        false
      );
    } catch (error) {
      console.error(error);
    }
  };

  getActiveStoreCityWithFields = async (): Promise<void> => {
    await queryClient
      .ensureQueryData(activeStoreCitiesQueryOptions())
      .then(res => {
        const regions = res?.getActiveStoreCitiesWithFields?.filter(
          item => Number(item.id) === MOSCOW_REGION_ID
        );
        if (regions?.length) {
          this.regions = regions;
        }
      });
  };

  getBarMenuCart = async ({
    token = '',
    withoutCartToken = false,
    menuPeopleId = null
  }: GetBarMenuCartParams = {}): Promise<void> => {
    const barId = location?.pathname?.split('/')?.[2];
    await when(() => !this.isShopsLoading);

    const foundShop = this.openedShops.find(
      item => `${item?.identify}` === barId || `${item?.id}` === barId
    );
    if (!foundShop?.id) {
      return;
    }

    await this.getCart({
      newToken: withoutCartToken
        ? ''
        : token || getSavedBarMenuCartToken() || cartToken,
      store_id: Number(foundShop.id),
      withoutCartToken,
      menuPeopleId
    });
    syncFlocktoryCart(this.cart);
  };

  resetStore = (): void => {
    XStore.resetStore();
    Object.assign(this, {
      ...initialState,
      cart: {
        ...(initialState.cart || {}),
        store_id: this.regionData?.default_store_id ?? MOSCOW_DEFAULT_STORE_ID
      },
      regions: this.regions,
      shops: this.shops,
      isShopsLoading: this.isShopsLoading
    });
  };

  initCartStore = async ({
    token,
    pathname,
    resolveUserMenuCart = false,
    menuPeopleId = null
  }: InitCartStoreParams): Promise<void> => {
    if (pathname.includes('/bar-menu/')) {
      const barMenuParams = {
        token,
        withoutCartToken: resolveUserMenuCart,
        menuPeopleId
      };
      switch (this.cartType) {
        case 'default': {
          this.resetStore();
          await this.getBarMenuCart(barMenuParams);
          this.setCartType('bar-menu');
          break;
        }
        case 'bar-menu': {
          if (token || resolveUserMenuCart) {
            await this.getBarMenuCart(barMenuParams);
          }
          break;
        }
        default: {
          return;
        }
      }
    } else {
      if (
        this.cartType === 'bar-menu' ||
        !this.isLoadCart ||
        (token && this.cartType === 'default')
      ) {
        this.resetStore();
        await this.getDefaultCart(token);
        this.setCartType('default');
      }
    }
  };

  getCart = async ({
    recalculate = false,
    newToken = '',
    store_id = 0,
    withoutCartToken = false,
    menuPeopleId = null
  }: GetCartParams): Promise<CartT> => {
    const cartCounter = this.cartCounter + 1;
    this.cartCounter = cartCounter;

    if (newToken && !withoutCartToken) {
      changeCartToken(newToken);
    }

    const request = store_id
      ? async () =>
          await fetchWithTimeout(
            async () =>
              await this.updateOrCreateMenuCart({
                store_id,
                cartToken: withoutCartToken ? undefined : cartToken,
                menuPeopleId
              })
          ).then(res => ({
            cart: res?.updateOrCreateMenuCart,
            services: []
          }))
      : async () =>
          await fetchWithTimeout(
            async () =>
              await APIold.cart({
                cartToken: newToken || cartToken,
                recalculate
              })
          ).then(res => ({
            cart: res.cart,
            services: res.servicesAvailableForPurchase
          }));

    return await request().then(res => {
      const { cart, services } = res;

      if (store_id && cart.cookieId && cart.is_menu) {
        changeCartToken(cart.cookieId);
        saveBarMenuCartToken(cart.cookieId);
      }
      if (this.cartCounter === cartCounter) {
        this.setCart(cart);

        this.updateConfig(
          {
            deliveryIntervalStart: cart.deliveryIntervalStart,
            deliveryIntervalEnd: cart.deliveryIntervalEnd
          },
          cart
        );
      }
      this.setServices(services);

      return cart;
    });
  };

  updateOrCreateMenuCart = async ({
    store_id,
    cartToken,
    menuPeopleId
  }: {
    store_id: number;
    cartToken?: string;
    menuPeopleId?: number | null;
  }): Promise<UpdateOrCreateMenuCartMutation> => {
    const peopleId = Number(menuPeopleId);

    return await APIold.updateOrCreateMenuCart({
      store_id,
      cartToken,
      menu_people_id: peopleId > 0 ? peopleId : null
    });
  };

  createMenuOrderFromCart = async ({
    table,
    phone
  }: {
    table: number;
    phone?: string;
  }): Promise<{
    key: string | null | undefined;
    orderId: number | null | undefined;
    error: string | null | undefined;
  }> => {
    const cartToken = this.cart?.cookieId || '';
    try {
      await APIStage.updateCart({
        cartToken,
        data: { paymentId: barMenuOnlinePaymentId }
      });
    } catch (error) {
      console.log(error);
    }
    return await APIold.createMenuOrderFromCart({
      cartToken,
      table,
      phone
    }).then(res => ({
      key: res.createMenuOrderFromCart?.order?.secretKey,
      orderId: res.createMenuOrderFromCart?.order?.id,
      error: res.createMenuOrderFromCart?.error
    }));
  };

  setShouldCheckExpress = (value: boolean): void => {
    this.shouldCheckExpress = value;
  };

  setAmountStores = (stores: GetAmountStoresQuery['getAmountStores']): void => {
    this.amountStores = stores || [];
  };

  setServices = (services: ServicesAvailableT): void => {
    this.availableServices = services || [];
  };

  setBillAmount = (amount: number): void => {
    if (this.cart?.people?.bill) {
      this.cart.people.bill.billAmount = amount;
    }
  };

  setCart = (cart: Partial<CartT>): void => {
    if (cart) {
      this.cart = cart !== null ? cart : DEFAULT_CART;
      this.setIsLoadCart(true);

      if (this.cartType === 'default') {
        this.modernTimer();
      }

      XStore.setXStoreId(cart.store_id || MOSCOW_DEFAULT_STORE_ID);

      this.setBillPayed(cart?.billPayed || 0);
      if (cart?.people?.bill?.billAmount) {
        this.setBillAmount(cart.people.bill.billAmount || 0);
      }

      const productsAmount: Record<number, number> = {};
      const productsAmountMultiple: Record<number, number> = {};
      const serviceAmount: Record<number, number> = {};
      const rubrics: Record<string, string> = {};

      const specialPrice: SpecialPriceItem[] = [];

      cart?.items
        ?.map(item => ({
          ...item,
          canBeBought: getProductCanBeBought({
            isProductMenu: !!item?.product?.isMenu,
            isNotForSale: item.product?.is_not_for_sale === 'Y',
            pathname: location.pathname
          })
        }))
        .filter(item => item?.product?.isAvailableForPurchase)
        .forEach(i => {
          if (i.id) {
            productsAmountMultiple[i.id] = i.amount || 0;
          }

          if (i?.product?.id && productsAmount?.[i.product.id]) {
            productsAmount[i.product.id] += i.amount || 0;
          } else {
            if (i?.product?.id) {
              productsAmount[i.product.id] = i.amount || 0;
            }
          }

          if (i?.product?.mainRootRubric?.id) {
            rubrics[i.product.mainRootRubric.id] =
              i.product.mainRootRubric.title || '';
          } else {
            rubrics.uncategorized = 'Без рубрики';
          }
        });

      cart?.services?.forEach(i => {
        if (i.id) {
          serviceAmount[i.id] = i.amount || 0;
        }
      });

      const specialPricePromo = cart?.promoCodes?.filter(
        item => item?.promoCode?.promoCodeType?.id === 9
      );
      if (specialPricePromo?.length) {
        specialPricePromo.forEach(item => {
          const element: SpecialPriceItem = {
            value: item.promoCode?.value,
            text: item.promoCode?.text,
            title: item.promoCode?.title,
            code: item.promoCode?.code,
            productId: item?.promoCode?.product?.id
          };
          specialPrice.push(element);
        });
      }

      if (cart.warning) {
        notifications(cart.warning, 'Внимание');
      }

      this.productsAmount = productsAmount;
      this.productsAmountMultiple = productsAmountMultiple;
      this.serviceAmount = serviceAmount;
      this.rubrics = rubrics;
      this.specialPrice = specialPrice;

      const setGifts = Number(cart?.gifts?.length) > 0;

      if (this.hasGift && !setGifts && !this.hasGiftInitial) {
        // Removed gift
        notifications('Подарок был удалён из Вашей корзины', 'Подарок удалён');
      } else if (!this.hasGift && setGifts && !this.hasGiftInitial) {
        // Add gift
        showGiftNotification(cart?.gifts?.[0]);
      }

      this.hasGift = setGifts;
      this.hasGiftInitial = false;
    } else {
      this.getCart({});
    }
  };

  setConfigFromConfigStore = (
    config: Pick<
      ConfigData,
      | 'default_delivery_morning_start'
      | 'default_delivery_morning_end'
      | 'default_delivery_evening_start'
      | 'default_delivery_evening_end'
    >
  ): Partial<ConfigValues> => {
    const newConfig: Partial<ConfigValues> = {
      morningStart: config?.default_delivery_morning_start,
      morningEnd: config?.default_delivery_morning_end,
      eveningStart: config?.default_delivery_evening_start,
      eveningEnd: config?.default_delivery_evening_end
    };

    this.setConfig(newConfig);

    return newConfig;
  };

  setConfig = (config: Partial<ConfigValues> | null): void => {
    if (config) {
      this.configValues = {
        ...this.configValues,
        ...config
      };
    } else {
      this.configValues = defaultConfig;
    }
  };

  updateConfig = (
    data: Omit<UpdateCartInput, 'cartToken'>,
    cart: UpdateCartMutation['updateCart']['cart']
  ): void => {
    if (
      data.deliveryIntervalStart !== undefined ||
      data.deliveryIntervalEnd !== undefined
    ) {
      const updatingValue: Partial<ConfigValues> = {};

      let deliveryIntervalId = cart?.deliveryIntervalId;
      if (data.deliveryIntervalId !== undefined) {
        deliveryIntervalId = data.deliveryIntervalId;
      }
      if (deliveryIntervalId === 2) {
        updatingValue.morningStart = `${data.deliveryIntervalStart}`;
        updatingValue.morningEnd = `${data.deliveryIntervalEnd}`;
      } else if (deliveryIntervalId === 3) {
        updatingValue.eveningStart = `${data.deliveryIntervalStart}`;
        updatingValue.eveningEnd = `${data.deliveryIntervalEnd}`;
      }

      const { morningStart, morningEnd, eveningStart, eveningEnd } =
        updatingValue;
      if (morningStart || morningEnd || eveningStart || eveningEnd) {
        this.setConfig(updatingValue);
      }
    }
  };

  updateCartMultiple = async (
    data: Omit<AddMultipleProductsToCartMutationVariables, 'cartToken'>
  ): Promise<void> => {
    const cartCounter = this.cartCounter + 1;
    this.cartCounter = cartCounter;
    await APIold.addMultipleProductsToCart({ cartToken, ...data }).then(res => {
      window?.dataLayer?.push?.({ event: 'webit_click_buyAll' });
      if (this.cartCounter === cartCounter) {
        if (res.addMultipleProductsToCart.cart) {
          this.setCart(res.addMultipleProductsToCart.cart);
          syncFlocktoryCart(res.addMultipleProductsToCart.cart);
        }
      }
    });
  };

  getUpdatedCart = async (
    data: Omit<AddProductToCartMutationVariables, 'cartToken'>,
    isDisabledNotification?: boolean
  ): Promise<AddProductToCartMutation> =>
    await fetchWithTimeout(
      async () =>
        await APIold.addProductToCart(
          { cartToken, ...data },
          isDisabledNotification
        )
    );

  removeCartItem = async (
    data: Pick<
      RemoveCartItemMutationVariables,
      'cartItemId' | 'continueAnyway'
    >,
    isDisabledNotification?: boolean
  ): Promise<RemoveCartItemMutation> =>
    await fetchWithTimeout(
      async () =>
        await APIold.removeCartItem(
          { cartToken, ...data },
          isDisabledNotification
        )
    );

  updateCart = async (
    productId: number,
    quantity: number,
    cartItemId?: number | undefined,
    isDisabledNotification?: boolean | undefined,
    continueAnyway: boolean = true,
    callback?: ((res: any) => void) | undefined
  ): Promise<void> => {
    // header animation
    document?.body?.classList?.add('cart-animation');
    setTimeout(() => {
      document?.body?.classList?.remove('cart-animation');
    }, 1000);
    // header animation

    const cartCounter = this.cartCounter + 1;
    this.cartCounter = cartCounter;

    const cartProductDebounceObj = this.cartProductDebounceFuncs[productId];

    if (
      !cartProductDebounceObj ||
      (cartProductDebounceObj &&
        !!quantity !== !cartProductDebounceObj.isRemove) ||
      (callback && !cartProductDebounceObj.callback)
    ) {
      this.cartProductDebounceFuncs[productId] = {
        isRemove: !quantity,
        callback,
        func: debounce(async (...args: Parameters<UpdateCartMainFunc>) => {
          await this.updateCartMainFunc(...args).then(res => {
            callback?.(res);
          });
        }, debounceTime)
      };
    }

    this.cartProductDebounceFuncs[productId].func(
      productId,
      quantity,
      continueAnyway,
      cartItemId,
      isDisabledNotification,
      cartCounter
    );
  };

  updateCartMainFunc: UpdateCartMainFunc = async (
    productId,
    quantity,
    continueAnyway = true,
    cartItemId,
    isDisabledNotification,
    cartCounter
  ) => {
    const product = this.cart?.items?.filter(
      item => item?.product?.id === +productId
    )[0];

    const params:
      | Omit<RemoveCartItemMutationVariables, 'cartToken'>
      | Omit<AddProductToCartMutationVariables, 'cartToken'> = {
      productId,
      quantity,
      continueAnyway,
      cartItemId: undefined as number | undefined
    };

    if (quantity) {
      return await this.getUpdatedCart(params, isDisabledNotification)
        .then(res => {
          if (this.cartCounter === cartCounter) {
            if (res.addProductToCart.cart) {
              this.setCart(res.addProductToCart.cart);
              syncFlocktoryCart(res.addProductToCart.cart);
            }
          }

          return res;
        })
        .then(res => {
          const productAfterAnswer = res.addProductToCart.cartItem;
          if (
            productAfterAnswer?.product?.postcardAllowed &&
            (!product ||
              Number(product.amount) < Number(productAfterAnswer.amount))
          ) {
            if (productAfterAnswer.id) {
              this.setIsOpenPostCardModal(
                productAfterAnswer.id,
                productAfterAnswer.postcards?.slice(
                  0,
                  Number(productAfterAnswer?.amount)
                )
              );
            }
          }
          if (productAfterAnswer?.product?.specialConditions) {
            if (
              res?.addProductToCart?.cartItem?.id &&
              !this.notificationQueue.has(res.addProductToCart.cartItem.id)
            ) {
              this.notificationQueue.set(
                res.addProductToCart.cartItem.id,
                productAfterAnswer.product.specialConditions
              );

              notifications(
                this.notificationQueue.get(res.addProductToCart.cartItem.id),
                'Внимание',
                5000
              );
            }
          }
          return res;
        });
    } else {
      params.cartItemId = Number(cartItemId) || product?.id;

      return await this.removeCartItem(
        {
          cartItemId: Number(params.cartItemId),
          continueAnyway: params.continueAnyway
        },
        isDisabledNotification
      )
        .then(res => {
          if (this.cartCounter === cartCounter) {
            if (res.removeCartItem.cart) {
              this.setCart(res.removeCartItem.cart);
              syncFlocktoryCart(res.removeCartItem.cart);
            }
          }

          return res;
        })
        .then(res => {
          if (
            res.removeCartItem.success &&
            location.pathname === '/cart/order'
          ) {
            const dadata = res.removeCartItem?.cart?.dadata;

            if (dadata?.geoLat && dadata?.geoLon) {
              this.setShouldCheckExpress(true);
              this.getDeliveryIntervals({
                lat: dadata.geoLat,
                lng: dadata.geoLon
              });
            }
          }
          if (params.cartItemId) {
            this.notificationQueue.delete(params.cartItemId);
          }
          return res;
        });
    }
  };

  setIsOpenPostCardModal = (
    isOpenPostCard: number | null,
    postcards?: CartPostcardsModel[],
    cartTokenEdit?: string | null,
    flowersAmount?: number | null
  ): void => {
    this.isOpenPostCard = isOpenPostCard;
    this.flowersPostcards = postcards || [];
    this.cartTokenEdit = cartTokenEdit || '';
    this.flowersAmount = flowersAmount || 0;
  };

  changeProductsAmount = (number: number, productId: number): void => {
    const oldAmount = this.productsAmount[productId] || 0;
    this.productsAmount[productId] = oldAmount + number;

    if (number >= 1) {
      increaseCounter(5736784, 'addProductToCartMain');
    }
  };

  // {id: 1, quantity: 1}
  bulkChangeProductsAmount = (products: BulkProductItem[]): void => {
    const initialObj = { ...this.productsAmount };
    const newProductsAmount = products.reduce(
      (acc, item) => ({
        ...acc,
        [item.productId]: item.quantity
      }),
      initialObj
    );
    this.productsAmount = newProductsAmount;
  };

  addProductToPostponed = async (
    productId: number,
    cartItemId?: number,
    isNeedRemove?: boolean
  ): Promise<
    AddProductToPostponedListMutation['addProductToPostponedList']
  > => {
    const cartCounter = this.cartCounter + 1;
    this.cartCounter = cartCounter;
    return await APIold.addProductToPostponed({ productId: +productId }).then(
      res => {
        if (cartItemId) {
          const params = {
            continueAnyway: true,
            cartItemId
          };

          if (!isNeedRemove) {
            this.removeCartItem(params).then(res => {
              if (this.cartCounter === cartCounter) {
                if (res.removeCartItem.cart) {
                  this.setCart(res.removeCartItem.cart);
                }
              }
              return res;
            });
          }
        }

        notifications('Товар успешно добавлен в отложенные', '');

        return res.addProductToPostponedList;
      }
    );
  };

  removeProductFromPostponed = async (
    productId: number
  ): Promise<
    RemoveProductFromPostponedListMutation['removeProductFromPostponedList']
  > =>
    await APIold.removeProductFromPostponed({ productId: +productId }).then(
      res => {
        notifications('Товар успешно удален из отложенных', '');

        return res.removeProductFromPostponedList;
      }
    );

  clearCart = (): void => {
    const cartCounter = this.cartCounter + 1;
    this.cartCounter = cartCounter;
    this.productsAmount = {};
    fetchWithTimeout(async () => await APIold.clearCart({ cartToken })).then(
      res => {
        this.setPromocodeInputValue('');
        if (this.cartCounter === cartCounter) {
          if (res.clearCart.cart) {
            this.setCart(res.clearCart.cart);
            syncFlocktoryCart(res.clearCart.cart);
          }
        }
      }
    );
  };

  saveTemplateCart = async (
    title: string
  ): Promise<SaveCartTemplateItemMutation> =>
    await API.saveCartTemplateItem({
      cartToken,
      title
    });

  setIsLoadCart = (isLoadCart: boolean): void => {
    this.isLoadCart = isLoadCart;
  };

  removeService = async (
    service: any,
    continueAnyway = false
  ): Promise<void> => {
    await APIold.removeCartService({
      cartToken,
      id: +service.id,
      continueAnyway
    }).then(() => {
      this.getCart({});
    });
  };

  addServiceToCart = (id: number, quantity: number): void => {
    if (!this.cartServicesDebounceFuncs[id]) {
      this.cartServicesDebounceFuncs[id] = debounce(
        async (...args: [number, number]) =>
          await this.addServiceToCartMain(...args),
        debounceTime
      );
    }

    this.cartServicesDebounceFuncs[id](id, quantity);
  };

  addServiceToCartMain = async (id: number, quantity: number): Promise<CartT> =>
    // const cartCounter = this.cartCounter + 1;
    // this.cartCounter = cartCounter;
    await APIold.addServiceToCart({ cartToken, id: +id, quantity }).then(
      async () => await this.getCart({})
      // if (this.cartCounter === cartCounter) {
      //   this.setCart(res.addServiceToCart.cart);
      // }
    );

  changeServicesAmount = (number: number, serviceId: number): void => {
    const oldAmount = this.serviceAmount[serviceId] || 0;
    this.serviceAmount[serviceId] = oldAmount + number;
  };

  payWithBonuses = async (): Promise<PayOrderWithBonusesMutation> => {
    const cartCounter = this.cartCounter + 1;
    this.cartCounter = cartCounter;
    return await APIold.payWithBonuses({
      cartToken,
      amount: this.billPayed || 0
    }).then(res => {
      if (this.cartCounter === cartCounter) {
        if (res.payOrderWithBonuses.cart) {
          this.setCart(res.payOrderWithBonuses.cart);
        }
      }
      return res;
    });
  };

  applyPromoCode = async (code: string): Promise<ApplyPromoCodeMutation> => {
    const cartCounter = this.cartCounter + 1;
    this.cartCounter = cartCounter;
    return await APIold.applyPromoCode({ cartToken, code }).then(res => {
      if (this.cartCounter === cartCounter) {
        if (res?.applyPromoCode?.cart) {
          this.setCart(res.applyPromoCode.cart);
        }
      }
      return res;
    });
  };

  removeCartPromoCode = (cartPromoCodeId: number): void => {
    const cartCounter = this.cartCounter + 1;
    this.cartCounter = cartCounter;

    APIold.removeCartPromoCode({
      cartToken,
      cartPromoCodeId: +cartPromoCodeId
    }).then(res => {
      this.setPromocodeInputValue('');
      if (this.cartCounter === cartCounter) {
        if (res?.removeCartPromoCode?.cart) {
          this.setCart(res.removeCartPromoCode.cart);
        }
      }
    });
  };

  getDeliveryIntervals = (coordinates: Coordinates, range?: Range[]): void => {
    const deliveryIntervalsCounter = this.deliveryIntervalsCounter + 1;
    this.deliveryIntervalsCounter = deliveryIntervalsCounter;
    const params: GetDeliveryIntervalsQueryVariables = {
      cartToken,
      coordinates,
      range: undefined as any[] | undefined
    };
    if (range) {
      if (range.length < 2 && range[0]?.id === 4) {
        let morningStart = this.configValues.morningStart;
        let morningEnd = this.configValues.morningEnd;
        let eveningStart = this.configValues.eveningStart;
        let eveningEnd = this.configValues.eveningEnd;

        if (
          morningStart === null ||
          eveningStart === null ||
          morningEnd === null ||
          eveningEnd === null
        ) {
          const configFromStore = ConfigStore.takeDeliveryValues();
          morningStart = configFromStore?.morningStart;
          morningEnd = configFromStore?.morningEnd;
          eveningStart = configFromStore?.eveningStart;
          eveningEnd = configFromStore?.eveningEnd;
        }
        params.range = [
          ...range,
          {
            id: 2,
            start: Number(morningStart),
            end: Number(morningEnd)
          },
          {
            id: 3,
            start: Number(eveningStart),
            end: Number(eveningEnd)
          }
        ];
      } else {
        params.range = range;
      }
    }

    APIStage.getDeliveryIntervals(params)
      .then(res => {
        if (this.deliveryIntervalsCounter === deliveryIntervalsCounter) {
          this.setIntervals(
            (res.getDeliveryIntervals?.intervals ||
              []) as unknown as DeliveryInterval[]
          );
          this.setExpressIntervals(res.getDeliveryIntervals?.express);
        }
        return {
          deliveryIntervals: res.getDeliveryIntervals.intervals,
          express: res.getDeliveryIntervals?.express
        };
      })

      // APIStage.deliveryIntervals(params)
      //   .then(res => {
      //     if (this.deliveryIntervalsCounter === deliveryIntervalsCounter) {
      //       this.setIntervals(res.flexibleDeliveryIntervals);
      //     }
      //     return res.flexibleDeliveryIntervals;
      //   })
      .then(({ deliveryIntervals, express }) => {
        // автовыбор экспрессе временно! отключен
        // if (this.shouldCheckExpress && !!express.length) {
        //   const expressData = express[0];
        //   const expressInterval = expressData?.intervals?.[0];

        //   if (!expressInterval) {
        //     this.setShouldCheckExpress(false);
        //     return;
        //   }

        //   const minWidth = expressInterval?.minWidth || 4;
        //   this.updateCartStage(
        //     {
        //       deliveryDate: expressInterval?.date,
        //       deliveryIntervalId: 4,
        //       deliveryIntervalStart: expressInterval?.start,
        //       deliveryIntervalEnd: expressInterval?.start + minWidth,
        //     },
        //     () => {
        //       setTimeout(() => {
        //         this.setShouldCheckExpress(false);
        //       }, 1000);
        //     }
        //   );

        //   return;
        // }

        if (this.shouldCheckExpress) {
          this.setShouldCheckExpress(false);
        }

        if (this.cart.deliveryIntervalId === 4) {
          if (
            this.cart.deliveryIntervalStart === null ||
            this.cart.deliveryIntervalEnd === null
          ) {
            if (express?.length) {
              const expressData = express[0];
              const expressInterval = expressData?.intervals?.[0];

              if (expressInterval) {
                const minWidth = expressInterval?.minWidth || 4;
                this.updateCartStage({
                  deliveryDate: expressInterval?.date || expressData?.value,
                  deliveryIntervalId: 4,
                  deliveryIntervalStart: expressInterval?.start,
                  deliveryIntervalEnd: Number(expressInterval?.start) + minWidth
                });
              }
            }
          }
          return;
        }

        if (
          !this.cart.deliveryIntervalId ||
          this.cart.deliveryIntervalStart === null ||
          this.cart.deliveryIntervalEnd === null
        ) {
          const dayOfWeek =
            deliveryIntervals?.findIndex(
              item => this.cart.deliveryDate === item.value
            ) !== -1
              ? deliveryIntervals?.findIndex(
                  item => this.cart.deliveryDate === item.value
                )
              : deliveryIntervals?.findIndex(item => item?.intervals?.length);

          if (
            dayOfWeek !== undefined &&
            dayOfWeek >= 0 &&
            deliveryIntervals?.[dayOfWeek]?.available
          ) {
            if (
              this.configValues.morningStart === null ||
              this.configValues.eveningStart === null ||
              this.configValues.morningEnd === null ||
              this.configValues.eveningEnd === null
            ) {
              const { morningStart, morningEnd, eveningStart, eveningEnd } =
                ConfigStore.takeDeliveryValues();

              this.updateCartStage({
                deliveryDate: deliveryIntervals[dayOfWeek].value,
                deliveryIntervalId:
                  deliveryIntervals[dayOfWeek].intervals?.[0]?.id,
                deliveryIntervalStart:
                  deliveryIntervals[dayOfWeek].intervals?.[0]?.id === 2
                    ? Number(morningStart)
                    : Number(eveningStart),
                deliveryIntervalEnd:
                  deliveryIntervals[dayOfWeek]?.intervals?.[0]?.id === 2
                    ? Number(morningEnd)
                    : Number(eveningEnd)
                // deliveryIntervalStart: deliveryIntervals[dayOfWeek].intervals[0].start,
                // deliveryIntervalEnd: deliveryIntervals[dayOfWeek].intervals[0].end
              });
            } else {
              this.updateCartStage({
                deliveryDate: deliveryIntervals[dayOfWeek].value,
                deliveryIntervalId:
                  deliveryIntervals[dayOfWeek]?.intervals?.[0]?.id,
                deliveryIntervalStart:
                  deliveryIntervals[dayOfWeek]?.intervals?.[0]?.id === 2
                    ? Number(this.configValues.morningStart)
                    : Number(this.configValues.eveningStart),
                deliveryIntervalEnd:
                  deliveryIntervals[dayOfWeek]?.intervals?.[0]?.id === 2
                    ? Number(this.configValues.morningEnd)
                    : Number(this.configValues.eveningEnd)
                // deliveryIntervalStart: deliveryIntervals[dayOfWeek].intervals[0].start,
                // deliveryIntervalEnd: deliveryIntervals[dayOfWeek].intervals[0].end
              });
            }
          }
        }
      });
  };

  setIntervals = (intervals?: DeliveryInterval[]): void => {
    this.deliveryIntervals = (intervals || []).map(interval => {
      const recommendedTime = interval?.recommendedTime;
      if (!recommendedTime?.length) {
        return interval;
      }

      const seen = new Set<string>();
      const uniqueRecommendedTime: NonNullable<
        DeliveryInterval['recommendedTime']
      > = [];

      for (const item of recommendedTime) {
        if (!item?.time || seen.has(item.time)) continue;
        seen.add(item.time);
        uniqueRecommendedTime.push(item);
      }

      return {
        ...interval,
        recommendedTime: uniqueRecommendedTime
      };
    });
  };

  setExpressIntervals = (
    express?: GetDeliveryIntervalsQuery['getDeliveryIntervals']['express']
  ): void => {
    this.expressIntervals = express || [];
  };
  // getNotAvailableGifts = value => this.deliveryIntervals.find(i => i.value === value).notAvailableGifts;

  todayLatestInterval = async (): Promise<TodayLatestIntervalQuery> =>
    await APIStage.todayLatestInterval({
      cartToken
    });

  getPaymentOptions = async (
    params: Omit<GetPaymentOptionsQueryVariables, 'cartToken'>
  ): Promise<GetPaymentOptionsQuery> =>
    await APIStage.getPaymentOptions({ ...params, cartToken }).then(res => {
      const sberpayPaymentObj = {
        id: 10,
        identify: 'sberpay',
        maxPrice: 99000,
        minPrice: 0,
        title: 'SberPay',
        subTitle: 'Быстрая оплата в приложении <br> СберБанк Онлайн',
        bonus_percent: null,
        possibleExtraBonus: null
      };
      const paymentRes = res.paymentOptions.slice().map(item => {
        if (item.id === 19) {
          // <span> <span> +{+item.possibleExtraBonus} бонусов</span> при оплате по СБП</span>
          return {
            ...item,
            title: 'СБП',
            withBonusTitle:
              Number(item?.possibleExtraBonus) > 0 ? 'при оплате по СБП' : 'СБП'
          };
        }
        return item;
      });

      if (paymentRes.find(item => item.identify === 'sberbank')) {
        paymentRes.splice(2, 0, sberpayPaymentObj);
      }

      // const sbpInArray = paymentRes.find(i => i.identify === 'sbp');
      // const sbpIndex = paymentRes.findIndex(i => i.identify === 'sbp');
      // position sbp first in array
      // if (sbpIndex && sbpInArray) {
      //   paymentRes.splice(sbpIndex, 1);
      //   paymentRes.splice(0, 0, sbpInArray);
      // }

      this.setPaymentOptions(paymentRes);
      return res;
    });

  setPaymentOptions = (options: PaymentOption[]): void => {
    this.paymentOptions = options;
  };

  selectPickupStore = async (
    storeId: number,
    pickupCityId?: number | null,
    deliveryId: number = 5
  ): Promise<UpdateCartMutation> => {
    const currentRegionId = this.regionData?.id;
    const targetRegion =
      pickupCityId !== null && pickupCityId !== undefined
        ? this.regions.find(item => Number(item.id) === Number(pickupCityId))
        : undefined;

    const isOtherRegion =
      !!targetRegion &&
      currentRegionId !== null &&
      currentRegionId !== undefined &&
      Number(targetRegion.id) !== Number(currentRegionId);

    if (isOtherRegion && targetRegion?.default_store_id) {
      await this.updateCartStage({ storeId: targetRegion.default_store_id });
    }

    return await this.updateCartStage({ storeId, deliveryId });
  };

  updateCartStage = async (
    data: Omit<UpdateCartMutationVariables['data'], 'cartToken'>,
    func?: () => void
  ): Promise<UpdateCartMutation> => {
    const cartCounter = this.cartCounter + 1;
    this.cartCounter = cartCounter;
    if (data?.deliveryDate) {
      let dayOfWeek;

      if (
        this.deliveryIntervals?.findIndex(
          item => data.deliveryDate === item.value
        ) === -1
      ) {
        dayOfWeek = this.deliveryIntervals.findIndex(item => item.available);
      } else {
        dayOfWeek = this.deliveryIntervals.findIndex(
          item => data.deliveryDate === item.value
        );
      }

      if (dayOfWeek >= 0) {
        this.setDayOfWeek(dayOfWeek);
      }
    }

    return await APIStage.updateCart({
      cartToken,
      data
    })
      .then(res => {
        if (this.cartCounter === cartCounter) {
          if (res.updateCart.cart) {
            this.setCart(res.updateCart.cart);
          }

          this.updateConfig(data, res.updateCart.cart);
        }
        return res;
      })
      .then(res => {
        if (func) {
          func();
        }

        return res;
      });
  };

  updatePackets = (isLessContainers: boolean): void => {
    this.updateCartStage({
      lessContainers: isLessContainers
    });
  };

  getNearestDeliveryInterval = (): void => {
    API.getDefaultNearestDelivery().then(res => {
      this.setNearestDeliveryInterval(res.getDefaultNearestDelivery);
    });
  };

  setNearestDeliveryInterval = (
    nearestDeliveryInterval: NearestDeliveryInterval
  ): void => {
    this.nearestDeliveryInterval = nearestDeliveryInterval;
  };

  createOrderFromCart = async (params: {
    data: CreateOrderFromCartMutationVariables['data'];
    isDuplicateRequest?: boolean;
  }): Promise<CreateOrderFromCartMutation> =>
    await APIStage.createOrderFromCart({
      cartToken,
      data: params.data,
      isDuplicateRequest: params.isDuplicateRequest
    }).then(res => {
      if (res?.createOrderFromCart?.success) {
        this.hasGift = false;
        this.hasGiftInitial = true;
      }

      return res;
    });

  // getMaxAvailableQuantityInOneOrder = date => {
  //   return APIStage.checkMaxAvailableQuantityInOneOrder({
  //     cartToken,
  //     date
  //   })
  //     .then(res => {
  //       // this.setMaxAvailableQuantityInOneOrder(res.checkMaxAvailableQuantityInOneOrder);
  //       return res.checkMaxAvailableQuantityInOneOrder;
  //     });
  // }
  // setMaxAvailableQuantityInOneOrder = maxAvailableQuantityInOneOrder => {
  //   this.maxAvailableQuantityInOneOrder = maxAvailableQuantityInOneOrder;
  // }

  repeatOrder = async (orderId: number, latest?: boolean): Promise<void> => {
    const cartCounter = this.cartCounter + 1;
    this.cartCounter = cartCounter;

    await APIStage.addItemsToCartFromOrder({
      cartToken,
      orderId: +orderId,
      latest: !!latest
    }).then(res => {
      if (this.cartCounter === cartCounter) {
        if (res.addItemsToCartFromOrder.cart) {
          this.setCart(res.addItemsToCartFromOrder.cart);
        }
      }

      increaseCounter(5736784, 'repeatOrder');
    });
  };

  addItemsToCartFromTemplate = async (id: number): Promise<void> => {
    const cartCounter = this.cartCounter + 1;
    this.cartCounter = cartCounter;
    await APIStage.addItemsToCartFromTemplate({
      cartToken,
      id
    }).then(res => {
      if (this.cartCounter === cartCounter) {
        if (res.addItemsToCartFromTemplate.cart) {
          this.setCart(res.addItemsToCartFromTemplate.cart);
        }
      }
    });
  };

  mergeCarts = async (
    data: MergeCartsMutationVariables,
    menuPeopleId: number | null = null
  ): Promise<MergeCartsMutation> => {
    const cartCounter = this.cartCounter + 1;
    this.cartCounter = cartCounter;

    const params: MergeCartsMutationVariables = {
      ...data
    };
    if (!data.sourceCartToken) {
      params.sourceCartToken = cartToken;
    }
    if (!data.targetCartToken) {
      params.targetCartToken = cartToken;
    }
    return await APIStage.mergeCarts(params).then(async res => {
      if (
        this.cartCounter === cartCounter &&
        res.mergeCarts.success &&
        res.mergeCarts.cart
      ) {
        const targetToken = params.targetCartToken;
        const isOrderEditPage = location.pathname.includes('/cabinet/orders/');

        if (targetToken && !isOrderEditPage) {
          const store_id =
            this.cartType === 'bar-menu' ? this.cart?.store_id || 1 : 0;
          if (store_id) {
            changeCartToken(targetToken);
          } else {
            persistCartToken(targetToken);
          }
          return await this.getCart({
            newToken: targetToken,
            store_id,
            menuPeopleId
          }).then(() => {
            this.changeMergeState(true);
            return res;
          });
        } else {
          return await this.getCart({}).then(() => {
            this.changeMergeState(true);
            return res;
          });
        }
      }
      this.changeMergeState(true);
      return res;
    });
  };

  getRecommendationsToCart = async (): Promise<GetRecommendationsToCartQuery> =>
    await API.getRecommendationsToCart({ cartToken });

  updateCartLoyaltyProgram = (
    data: Omit<UpdateCartLoyaltyProgramMutationVariables, 'cartToken'>
  ): void => {
    const cartCounter = this.cartCounter + 1;
    this.cartCounter = cartCounter;
    APIStage.updateCartLoyaltyProgram({
      ...data,
      cartToken
    }).then(res => {
      if (this.cartCounter === cartCounter) {
        if (res.updateCartLoyaltyProgram.cart) {
          this.setCart(res.updateCartLoyaltyProgram.cart);
        }
      }
    });
  };

  oneClickOrderCallback = async (
    data: UpdateCartMutationVariables['data']
  ): Promise<CreateOrderFromCartMutation | undefined> => {
    const cartCounter = this.cartCounter + 1;
    this.cartCounter = cartCounter;
    return await APIStage.updateCart({
      cartToken,
      data
    }).then(async res => {
      if (this.cartCounter === cartCounter) {
        if (res.updateCart.cart) {
          this.setCart(res.updateCart.cart);
        }
        const newOrder = this.createOrderFromCart({
          data: {
            source: 'oneClick'
          }
        });
        return await newOrder;
      }
    });
  };

  withCartToken = <T extends keyof ApiT>(
    data: Parameters<ApiT[T]>[0]['data'],
    method: T
  ): ReturnType<ApiT[T]> => (API as ApiT)[method]({ cartToken, data } as any);

  withCartTokenSpread = async <T extends keyof ApiT>(
    data: Omit<Parameters<ApiT[T]>[0], 'cartToken'>,
    method: T
  ): Promise<Awaited<ReturnType<ApiT[T]>>> =>
    (API as ApiT)[method]({ cartToken, ...data } as any);

  // configRequests = () => {

  //   const list = [
  //     'default_delivery_morning_start',
  //     'default_delivery_morning_end',
  //     'default_delivery_evening_start',
  //     'default_delivery_evening_end'
  //   ];

  //   return APIStage.configValueList(list).then(result => {
  //     const dataAdapter = [];

  //     list.map(a => {
  //       dataAdapter.push({
  //         configValue: {
  //           value: result?.[a]?.value
  //         }
  //       });
  //     });

  //     this.setConfig({
  //       morningStart: dataAdapter[0].configValue.value,
  //       morningEnd: dataAdapter[1].configValue.value,
  //       eveningStart: dataAdapter[2].configValue.value,
  //       eveningEnd: dataAdapter[3].configValue.value
  //     });

  //     return dataAdapter;
  //   });

  // const configRequests = [
  //   APIStage.configValue({ name: 'default_delivery_morning_start' }),
  //   APIStage.configValue({ name: 'default_delivery_morning_end' }),
  //   APIStage.configValue({ name: 'default_delivery_evening_start' }),
  //   APIStage.configValue({ name: 'default_delivery_evening_end' })
  // ];

  // return Promise.all(configRequests)
  //   .then(res => {
  //     this.setConfig({
  //       morningStart: res[0].configValue.value,
  //       morningEnd: res[1].configValue.value,
  //       eveningStart: res[2].configValue.value,
  //       eveningEnd: res[3].configValue.value
  //     });

  //     return res;
  //   });
  // }

  confirmProfileBySMS = async (
    data: Omit<ConfirmProfileBySmsMutationVariables, 'cartToken'>
  ): Promise<ConfirmProfileBySmsMutation> =>
    await APIStage.confirmProfileBySMS({
      cartToken,
      ...data
    });

  setDayOfWeek = (dayOfWeek: number): void => {
    this.dayOfWeek = dayOfWeek;
  };

  updateCartStageWithDay = async (
    params: Omit<UpdateCartMutationVariables['data'], 'cartToken'>,
    dayOfWeek: number
  ): Promise<UpdateCartMutation> => {
    this.setDayOfWeek(dayOfWeek);
    return await this.updateCartStage(params);
  };

  cancelTestDriveApplication = (testDriveId: number): void => {
    const cartCounter = this.cartCounter + 1;
    this.cartCounter = cartCounter;
    APIold.cancelTestDriveApplication({
      cartToken,
      testDriveId: +testDriveId
    }).then(res => {
      if (this.cartCounter === cartCounter) {
        if (res.cancelTestDriveApplication.cart) {
          this.setCart(res.cancelTestDriveApplication.cart);
        }
      }
    });
  };

  changeMergeState = (value: boolean): void => {
    this.isMerged = value;
  };

  shareCart = async (): Promise<ShareCartQuery> =>
    await APIold.shareCart({ cartToken });

  rebindCartPid = (): void => {
    if (location.pathname.includes('/bar-menu/')) {
      return;
    }
    const cartTokenLocalStorage = localStorage.getItem('headerToken');
    if (!cartTokenLocalStorage) {
      return;
    }
    const peopleToken = JSON.parse(cartTokenLocalStorage) as string;
    if (!peopleToken) {
      return;
    }
    API.rebindCartPid({ cartToken, peopleToken }).then(() => {
      this.getCart({});
    });
  };

  onAddPostCard = async (postcards: CartPostcardInput[]): Promise<void> => {
    const cartCounter = this.cartCounter + 1;
    this.cartCounter = cartCounter;
    await APIold.addCartItemPostcard({
      cartToken: this.cartTokenEdit || cartToken,
      postcards
    }).then(res => {
      if (this.cartCounter === cartCounter) {
        if (res?.addCartItemPostcard?.cart) {
          this.setCart(res.addCartItemPostcard.cart);
        }
      }
      this.setIsOpenPostCardModal(null);
    });
  };

  checkCartChanges = async (): Promise<CheckCartChangesQuery> =>
    await APIStage.checkCartChanges({ cartToken });

  getReplacedProductCart = async (): Promise<GetReplacedProductCartQuery> =>
    await APIold.getReplacedProductCart({ cartToken });

  setSelectExpressDel = (value: boolean): void => {
    this.isSelectExpressDel = value;
  };
}

const cartStore = new Store();
export type CartStoreType = typeof cartStore;
export default cartStore;
